import { AudioOutlined, DownloadOutlined } from '@ant-design/icons';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { PublicLifeStory } from 'api/LifeReviewApi';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { buildBrandedInviteSignupHref } from 'utils/brandedInviteSignup';
import './FamilyViewRenderer.css';

type NormalizedResponse = {
  id?: string;
  promptText?: string;
  text?: string;
  audioUrl?: string;
  sectionTitle?: string;
  sectionIndex?: number;
  responseIndex?: number;
  recordedAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

type NormalizedSection = {
  id?: string;
  key?: string;
  title?: string;
  description?: string;
  responses: NormalizedResponse[];
};

const SECTION_DESCRIPTION_BY_KEY: Record<string, string> = {
  'early-life': 'Childhood memories, the home you grew up in, and the world around you.',
  'family-relationships': 'The people you loved, leaned on, and shared life with.',
  'career-work': 'The work you did, why you did it, and what it meant to you.',
  'lessons-advice': 'What life taught you, and what you hope others carry forward.',
  'faith-values': 'Beliefs, values, and perspective that shaped your life.',
  'legacy-messages': 'What you hope to leave behind for the people you love.',
  'military-service-veterans':
    'Stories of service, sacrifice, and the memories that stayed with you after military life.'
};

const normalizeLifeReviewSectionKey = (key?: string) => {
  if (!key) {
    return '';
  }

  return key
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');
};

const getSectionDescription = (section: NormalizedSection) => {
  if (section.description) {
    return section.description;
  }

  const normalizedKey = normalizeLifeReviewSectionKey(section.key || section.title);
  if (!normalizedKey) {
    return '';
  }

  return SECTION_DESCRIPTION_BY_KEY[normalizedKey] || section.title || '';
};

const formatStoryDate = (response: NormalizedResponse) => {
  const value = response.recordedAt || response.createdAt || response.updatedAt;
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric'
  });
};

export default function FamilyViewRenderer({
  token,
  lifeStory,
  patientName,
  firstName,
  showAccountPrompt,
  tenantOrganizationName,
  tenantOrganizationLogoUrl,
  sections,
  onAudioError
}: {
  token: string;
  lifeStory?: PublicLifeStory;
  patientName: string;
  firstName: string;
  showAccountPrompt?: boolean;
  tenantOrganizationName?: string;
  tenantOrganizationLogoUrl?: string;
  sections: NormalizedSection[];
  onAudioError: () => void;
}) {
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [isZipDownloading, setIsZipDownloading] = useState(false);
  const [zipDownloadError, setZipDownloadError] = useState('');
  const [activeChapterId, setActiveChapterId] = useState('all');
  const location = useLocation();
  const downloadMenuRef = useRef<HTMLDivElement | null>(null);
  const organizationName = tenantOrganizationName?.trim();
  const organizationLogoUrl = tenantOrganizationLogoUrl?.trim();
  const signupBaseUrl = lifeStory?.signupUrl?.trim() || process.env.REACT_APP_MAIN_WEBSITE_URL;
  const signupHref = buildBrandedInviteSignupHref(location, signupBaseUrl);

  const chapterOptions = useMemo(
    () =>
      sections
        .filter((section) => section.responses.length > 0)
        .map((section, index) => ({
          id: section.id || section.key || section.title || `section-${index}`,
          title: section.title || `Chapter ${index + 1}`
        })),
    [sections]
  );

  const visibleSections = useMemo(() => {
    if (activeChapterId === 'all') {
      return sections;
    }

    return sections.filter(
      (section, index) => (section.id || section.key || section.title || `section-${index}`) === activeChapterId
    );
  }, [activeChapterId, sections]);

  useEffect(() => {
    if (!isDownloadMenuOpen) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (downloadMenuRef.current?.contains(target)) {
        return;
      }

      setIsDownloadMenuOpen(false);
    };

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isDownloadMenuOpen]);

  useEffect(() => {
    if (activeChapterId !== 'all' && !chapterOptions.some((chapter) => chapter.id === activeChapterId)) {
      setActiveChapterId('all');
    }
  }, [activeChapterId, chapterOptions]);

  const sanitizeFilenamePart = (value?: string, fallback = 'Item') => {
    const safe = (value || fallback)
      .trim()
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 40);

    return safe || fallback;
  };

  const getAudioExtension = (audioUrl?: string) => {
    if (!audioUrl) {
      return 'mp3';
    }

    const cleaned = audioUrl.split('?')[0] || '';
    const ext = cleaned.split('.').pop()?.toLowerCase();
    if (!ext) {
      return 'mp3';
    }

    if (['mp3', 'wav', 'webm', 'ogg', 'm4a', 'mp4'].includes(ext)) {
      return ext;
    }

    return 'mp3';
  };

  const downloadFile = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const objectUrl = URL.createObjectURL(blob);
    downloadFile(objectUrl, fileName);
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 1000);
  };

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const getFilenameFromContentDisposition = (contentDisposition?: string | null) => {
    if (!contentDisposition) {
      return '';
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]).replace(/^"|"$/g, '').trim();
    }

    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1]?.trim() || '';
  };

  const getAudioFileName = (response: NormalizedResponse, index: number) => {
    const sectionPart = sanitizeFilenamePart(response.sectionTitle, 'Life_Story');
    const promptPart = sanitizeFilenamePart(response.promptText, 'Recording');
    const number = String(index + 1).padStart(2, '0');
    const extension = getAudioExtension(response.audioUrl);

    return `${number}_${sectionPart}_${promptPart}.${extension}`;
  };

  const audioResponses = useMemo(
    () => sections.flatMap((section) => section.responses).filter((response) => Boolean(response.audioUrl)),
    [sections]
  );

  const handleDownloadAllAudio = async () => {
    for (let index = 0; index < audioResponses.length; index += 1) {
      const response = audioResponses[index];
      if (!response?.audioUrl) {
        continue;
      }

      try {
        const fileName = getAudioFileName(response, index);
        const fetchResponse = await fetch(response.audioUrl);
        if (fetchResponse.ok) {
          const responseBlob = await fetchResponse.blob();
          const objectUrl = URL.createObjectURL(responseBlob);
          downloadFile(objectUrl, fileName);
          setTimeout(() => {
            URL.revokeObjectURL(objectUrl);
          }, 1000);
        } else {
          downloadFile(response.audioUrl, getAudioFileName(response, index));
        }
      } catch {
        downloadFile(response.audioUrl, getAudioFileName(response, index));
      }

      await delay(150);
    }
  };

  const handleDownloadWrittenLifeReview = () => {
    setIsDownloadMenuOpen(false);
    setTimeout(() => {
      window.print();
    }, 0);
  };

  const handleDownloadAllAudioFromMenu = () => {
    setIsDownloadMenuOpen(false);
    handleDownloadAllAudio();
  };

  const handleDownloadZip = async () => {
    if (!token || isZipDownloading) {
      return;
    }

    setIsDownloadMenuOpen(false);
    setIsZipDownloading(true);
    setZipDownloadError('');

    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL;
      const downloadUrl = apiBaseUrl
        ? new URL(`/public/life-story/${encodeURIComponent(token)}/download`, apiBaseUrl).toString()
        : `/public/life-story/${encodeURIComponent(token)}/download`;

      const response = await fetch(downloadUrl, {
        method: 'GET'
      });

      if (response.ok) {
        const buffer = await response.arrayBuffer();
        const contentType = response.headers.get('Content-Type') || 'application/zip';
        const blob = new Blob([buffer], { type: contentType });
        const fileNameFromHeader = getFilenameFromContentDisposition(response.headers.get('Content-Disposition'));
        const fileName = fileNameFromHeader || 'Life-Story-Keepsake.zip';
        downloadBlob(blob, fileName);
      } else {
        setZipDownloadError('We couldn’t prepare the download. Please try again.');
      }
    } catch {
      setZipDownloadError('We couldn’t prepare the download. Please try again.');
    } finally {
      setIsZipDownloading(false);
    }
  };

  return (
    <div className="life-story-family-view">
      <header className="life-story-page-header">
        <div className="life-story-page-header-left">
          {organizationLogoUrl && (
            <img src={organizationLogoUrl} alt="" className="life-story-page-header-logo" aria-hidden="true" />
          )}
          <p className="life-story-page-header-org-subtitle">Caring for life's final chapter</p>
        </div>

        <div className="life-story-page-header-actions">
          <div className="life-story-page-header-download-wrap" ref={downloadMenuRef}>
            <button
              type="button"
              className="life-story-page-header-download"
              onClick={() => setIsDownloadMenuOpen((prev) => !prev)}
              aria-haspopup="menu"
              aria-expanded={isDownloadMenuOpen}
            >
              <DownloadOutlined />
              <span>Download</span>
            </button>

            {isDownloadMenuOpen && (
              <div className="life-story-page-header-download-menu" role="menu" aria-label="Download options">
                <button type="button" role="menuitem" onClick={handleDownloadZip} disabled={isZipDownloading || !token}>
                  {isZipDownloading ? 'Preparing download...' : 'Download ZIP'}
                </button>
                <button type="button" role="menuitem" onClick={handleDownloadWrittenLifeReview}>
                  Download text
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleDownloadAllAudioFromMenu}
                  disabled={!audioResponses.length}
                >
                  Download audio
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {isZipDownloading && <p className="life-story-download-status">Preparing download...</p>}
      {zipDownloadError && <p className="life-story-download-error">{zipDownloadError}</p>}

      <header className="life-story-hero">
        <div className="life-story-hero-heart" aria-hidden="true">
          <HeartIconSolid className="life-story-hero-heart-icon" />
        </div>
        <p className="life-story-hero-kicker">The Life & Stories Of</p>
        <h1>{patientName}</h1>
        <p className="life-story-hero-subtitle">
          {lifeStory?.heroSubtitle ||
            'A collection of memories, lessons, and reflections — shared in their own voice, preserved for the people they loved.'}
        </p>

        {chapterOptions.length > 0 && (
          <div className="life-story-chapters" aria-label="Chapters">
            <button
              type="button"
              className={`life-story-chapter-button ${activeChapterId === 'all' ? 'is-active' : ''}`}
              onClick={() => setActiveChapterId('all')}
            >
              All chapters
            </button>
            {chapterOptions.map((chapter) => (
              <button
                key={chapter.id}
                type="button"
                className={`life-story-chapter-button ${activeChapterId === chapter.id ? 'is-active' : ''}`}
                onClick={() => setActiveChapterId(chapter.id)}
              >
                {chapter.title}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="life-story-content">
        {visibleSections.map((section, sectionIndex) => (
          <section
            className="life-story-section"
            key={section.id || section.key || section.title || `section-${sectionIndex}`}
          >
            <div className="life-story-section-header">
              {section.title && <h2>{section.title}</h2>}
              {getSectionDescription(section) && (
                <p className="life-story-section-description">{getSectionDescription(section)}</p>
              )}
              <div className="life-story-section-divider" />
            </div>

            {section.responses.map((response, responseIndex) => {
              const storyDate = formatStoryDate(response);

              return (
                <article
                  className="life-story-response"
                  key={
                    response.id ||
                    `${section.id || section.key || section.title}-${response.responseIndex ?? responseIndex}`
                  }
                >
                  {response.promptText && (
                    <div className="life-story-question-row">
                      <svg
                        width="20"
                        height="16"
                        viewBox="0 0 20 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="life-story-question-icon"
                        aria-hidden="true"
                      >
                        <path d="M0 16V9.42857C0 6.57143 0.5 4.35714 1.5 2.78571C2.5 1.14286 4.16667 0.214286 6.5 0V3.14286C4.83333 3.35714 3.91667 4.57143 3.75 6.78571H6.5V16H0ZM11.1667 16V9.42857C11.1667 6.57143 11.6667 4.35714 12.6667 2.78571C13.6667 1.14286 15.3333 0.214286 17.6667 0V3.14286C16 3.35714 15.0833 4.57143 14.9167 6.78571H17.6667V16H11.1667Z" />
                      </svg>
                      <h3 className="life-story-question-title">{response.promptText}</h3>
                    </div>
                  )}

                  {response.text && <p className="life-story-response-text">“{response.text}”</p>}

                  {response.audioUrl && (
                    <div className="life-story-audio-wrap">
                      <div className="life-story-audio-label">
                        <AudioOutlined />
                        <span>Hear {firstName} tell this story</span>
                      </div>
                      <audio controls className="life-story-audio" onError={onAudioError}>
                        <source src={response.audioUrl} />
                      </audio>
                    </div>
                  )}

                  <div className="life-story-meta-row">
                    {response.audioUrl ? <span>Recorded in their voice</span> : <span>Recorded</span>}
                    {storyDate && <span>{response.audioUrl ? `· ${storyDate}` : storyDate}</span>}
                  </div>
                </article>
              );
            })}
          </section>
        ))}

        <footer className="life-story-footer">
          {showAccountPrompt && (
            <p className="life-story-footer-account-prompt">
              <a className="life-story-footer-account-link" href={signupHref}>
                Create a free Eternal account
              </a>{' '}
              to store these stories and revisit them anytime.
            </p>
          )}
          {organizationName && (
            <div className="life-story-footer-shared-by">
              {organizationLogoUrl && (
                <img src={organizationLogoUrl} alt="" className="life-story-footer-org-logo" aria-hidden="true" />
              )}
              <span>Shared by {organizationName}</span>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}
